import { INode } from '@/api/nodes/mutations/directory/create-directory-mutation'
import { useRenameNodeMutation } from '@/api/nodes/mutations/rename-node-mutation'
import { Button } from '@/components/ui/button'
import { GeneralModal, IconBox } from '@/components/ui/general-modal'
import { Input } from '@/components/ui/input'
import { Labeled } from '@/components/ui/labeled'
import { parseReadableNodeType } from '@/features/drive/core/utils/drive-helpers'
import { buildNodePath, useDirectoryContext } from '@/features/drive/entities/directory/directory-context'
import { Type } from 'lucide-react'
import { useCallback, useState } from 'react'

type INodeRenameState = {
    name: string
    emoji?: string
}

export type INodeRenameActionModalProps = {
    onClose: () => void
    node: INode
    isOpen: boolean
}

export const NodeRenameActionModal = ({ onClose, node, isOpen }: INodeRenameActionModalProps) => {
    const { nodePath } = useDirectoryContext()

    const [state, setState] = useState<INodeRenameState>({
        name: node.name,
        emoji: node.emoji || '',
    })

    const { mutateAsync: asyncRenameNode, isPending } = useRenameNodeMutation()

    const makeHandleStateChange = useCallback((key: keyof INodeRenameState) => {
        return (value: string) => {
            setState((prev) => ({ ...prev, [key]: value }))
        }
    }, [])

    const handleRenameNode = useCallback(() => {
        if (!node?.slug || !nodePath) return

        asyncRenameNode({
            nodePath: buildNodePath(nodePath, node.slug),
            newName: state.name,
            newEmoji: state.emoji || undefined,
        }).then(() => {
            onClose()
        })
    }, [node.slug, nodePath, asyncRenameNode, state.name, state.emoji, onClose])

    const readableType = parseReadableNodeType(node)

    return (
        <GeneralModal
            open={isOpen}
            onOpenChange={(open) => {
                if (!open) onClose()
            }}
            title={`Rename ${readableType}`}
            subtitle={`Give your ${(readableType ?? 'item').toLowerCase()} a new name.`}
            icon={
                <IconBox>
                    <Type className="h-5 w-5 text-stone-500" />
                </IconBox>
            }
        >
            <div className="flex flex-col gap-4">
                <div
                    className="flex flex-row gap-4"
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && state.name) {
                            handleRenameNode()
                        }
                    }}
                >
                    <Labeled
                        label={node.type === 'DIRECTORY' ? 'Folder name' : 'File name'}
                        styleType="small"
                        className="w-full"
                    >
                        <Input value={state.name} onValueChange={makeHandleStateChange('name')} />
                    </Labeled>

                    {node.type === 'DIRECTORY' && (
                        <Labeled label="Emoji (optional)" styleType="small">
                            <Input
                                autoComplete="off"
                                value={state.emoji}
                                onValueChange={(v) => {
                                    if (v.length > 2) return
                                    makeHandleStateChange('emoji')(v)
                                }}
                                className="bg-background"
                                placeholder="Select an emoji"
                            />
                        </Labeled>
                    )}
                </div>

                <div className="flex justify-end pt-2">
                    <Button
                        variant="ds2-primary"
                        size="ds2"
                        onClick={handleRenameNode}
                        isLoading={isPending}
                        disabled={isPending}
                    >
                        Save
                    </Button>
                </div>
            </div>
        </GeneralModal>
    )
}
